import React from 'react'
import ReactDOM from 'react-dom'
import deepmerge from 'deepmerge'
import { Auth, Chat, Config, FileTransfer, HTTP, Images, Logger, Messages, WS } from './managers/index'
import { IndexDB, Services } from './utils'
import './styles/global.css'
import App from './App'
import GD from './GD'

const MOBILE_WIDTH = 767

const defaultParams: IShared = {
  ep: 30,
  colors: 'light',
  role: 'payer',
  branch: 'live',
  token: undefined,
  payerToken: undefined,
  traderToken: undefined,
  viAuth: undefined,
  fxtoken: undefined,
  fxid: undefined,
  placeHolderID: 'root',
  headless: false,
  noClose: false,
  title: 'Support chat',
  mobile: true,
  promptMessage: 'Type your message...',
  bottomOffset: 25,
  rightOffset: 25,
  enableLog: false,
}

class Main {
  static async bootstrap() {
    await Main.setConfig()

    const urlParams: IShared = this.parseUrlParams(defaultParams)
    const scriptParams: IShared = this.parseScriptAttributes(defaultParams)
    const mergedParams: IShared = deepmerge(urlParams, scriptParams)

    this.doParamCorrection(mergedParams)

    GD.LOG.invoke(mergedParams)

    window.DCC_SHARED = mergedParams

    await new IndexDB().init()

    await this.initManagers()

    await this.renderApp(mergedParams)

    this.setGlobalSignals()
  }

  static validateConfig(config: IConfig): void {
    const requiredFields: Array<keyof IConfig> = [
      'API_BASE_URL',
      'API_FILES_URL',
      'WS_URL',
    ]
    
    requiredFields.forEach(el => {
      if (!(el in config)) {
        throw new Error(`${el} environment variable is not in config.json file in build root directory`)
      }
    })
  }

  static async setConfig(): Promise<void> {
    // Если конфиг уже загружен через dccloader.js, проверяем его и используем
    if (window.DCC_CONFIG) {
      this.validateConfig(window.DCC_CONFIG)
      window.DCC_VERSION = Config.version
      return
    }

    // Иначе загружаем конфиг самостоятельно (для обратной совместимости)
    // Если window.DCC_CONFIG уже существует, fetch не выполняется (проверка выше)
    if (!window.DCC_CONFIG) {
      let config: IConfig
      
      try {
        const response = await fetch('config.json')

        config = await response.json() as IConfig
        this.validateConfig(config)

        window.DCC_CONFIG = config
        window.DCC_CONFIG.ENVIRONMENT = config.ENVIRONMENT || 'live'
        window.DCC_CONFIG.LOGGER = config.LOGGER === undefined ? false : config.LOGGER
      }
      catch (e: unknown) {
        const message = 'Could not parse config.json. Make sure config file exist on public root'
        
        alert(message)
        console.error(e)
      }
    }

    window.DCC_VERSION = Config.version
  }

  static parseUrlParams(params: IShared): IShared {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams

    for (const [key, value] of searchParams.entries()) {
      const normalizedKey = key.replace(/^\s*|\s*$/gi, '').toLowerCase()
      const normalizedValue = value.replace(/^\s*|\s*$/gi, '')

      if (normalizedKey in params) {
        params[normalizedKey] = normalizedValue
      }
      else {
        const supportedKey = this.getSupportedKey(normalizedKey)
        if (supportedKey) params[supportedKey] = normalizedValue
      }
    }


    return params
  }

  static parseScriptAttributes(params: IShared): IShared {
    const shared = {}

    Object.entries(window.DCC_SHARED || {}).forEach(([key, value]) => {
      if (key in params) shared[key] = value
      else {
        const supportedKey = this.getSupportedKey(key)
        if (supportedKey) return shared[supportedKey] = value
      }
    })

    return Object.entries(shared).reduce((acc, [key, val]) => {
      acc[key] = val
      return acc
    }, {}) as IShared
  }

  static getSupportedKey(key: string): string | null {
    switch (key) {
      case 'placeholder-id': return 'placeHolderID'
      case 'prompt-message': return 'promptMessage'
      case 'bottom-offset': return 'bottomOffset'
      case 'right-offset': return 'rightOffset'
      case 'payer-auth-token': return 'payerToken'
      case 'trader-auth-token': return 'traderToken'
      case 'enable-log': return 'enableLog'
      case 'vi-auth': return 'viAuth'
      case 'no-close': return 'noClose'
      default:
        console.error(`Unknown script attribute: ${key}`)
        return null
    }
  }

  static doParamCorrection(params: IShared): void {
    if (params.token) {
      if (params.role === 'payer') params.payerToken = params.token
      if (params.role === 'trader') params.traderToken = params.token
    }

    if (params.branch) {
      params.branch = params.branch.toLowerCase() as BranchType
    }

    if (typeof params.headless === 'string' && params.headless) {
      params.headless = String(params.headless) === '1'
    }

    if (typeof params.mobile === 'string' && params.mobile) {
      const isMobile = window.innerWidth <= MOBILE_WIDTH

      params.mobile = isMobile || String(params.mobile) === '1'
    }

    if (typeof params.enableLog === 'string' && params.enableLog) {
      params.enableLog = String(params.enableLog) === '1'
    }
  }

  static async initManagers(): Promise<void> {
    return new Promise((resolve) => {
      const services = [Logger, Images, FileTransfer, WS, HTTP, Auth, Messages, Chat]

      let done = 0
      GD.SERVICE_READY.subscribe((name) => {
        done++

        GD.LOG.invoke('DCC -> Service initialized: ' + name)

        if (done === services.length) {
          resolve() // ALL SERVICES INITIALIZED!
        }
      })

      for (const i in services) new services[i]()
    })
  }

  static async renderApp(shared: IShared) {
    const rootElement = document.getElementById(shared.placeHolderID)

    if (!rootElement) {
      throw Error(`Could not find element to render widget: ${shared.placeHolderID}`)
    }

    ReactDOM.render(<App sharedData={shared} />, rootElement)
  }

  static setGlobalSignals() {
    window['DCC_GD'] = GD // REGISTERING GLOBAL DISPATCHER
    window['DCC_Services'] = Services // REGISTERING SERVICES

    window.DCC_openChat = () => {
      GD.GUI_OPEN_CHAT.invoke()
    }
    window.DCC_closeChat = () => {
      GD.GUI_CLOSE_CHAT.invoke()
    }

    GD.GUI_CHAT_OPENED.subscribe(() => {
      if (window.onDCC_ChatOpened && typeof window.onDCC_ChatOpened === 'function' && window.onDCC_ChatOpened.length === 0) {
        window.onDCC_ChatOpened()
      }
    })

    GD.GUI_CHAT_CLOSED.subscribe(() => {
      if (window.onDCC_ChatClosed && typeof window.onDCC_ChatClosed === 'function' && window.onDCC_ChatClosed.length === 0) {
        window.onDCC_ChatClosed()
      }
    })

    window.__DCC_help = () => {
      console.log(`
        Dukascopy Connect
        available callback, registering in global scope:

        onDCC_ChatLoaded() - invokes when Dukascopy Connect Chat Widget ready to work.
        onDCC_ChatOpened() - invokes when Dukascopy Connect Chat Widget opened.
        onDCC_ChatClosed() - invokes when Dukascopy Connect Chat Widget closed.

        available methods:
        DCC_openChat() - will open chat window
        DCC_closeChat() - will close chat window
        DCC_changeLanguage() - will change language (ISO 639-1)
      `)
    }

    if (window.onDCC_ChatLoaded && typeof window.onDCC_ChatLoaded === 'function' && window.onDCC_ChatLoaded.length === 0) {
      window.onDCC_ChatLoaded()
    }
  }
}

void Main.bootstrap()
